import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdGavel } from 'react-icons/md';

const LAST_UPDATED = '26 April 2026';
const COMPANY      = 'HCEN · Healthcare Emergency Network';
const EMAIL        = 'legal@hcen.in';

export default function TermsAndConditions() {
  const navigate = useNavigate();
  return (
    <div style={{ minHeight: '100vh', background: 'var(--auth-page-bg)', padding: '32px 20px', fontFamily: 'Outfit, sans-serif' }}>

      {/* Back button */}
      <div style={{ maxWidth: 820, margin: '0 auto 28px' }}>
        <button onClick={() => navigate(-1)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-muted)', border: '1px solid var(--surface-strong)', borderRadius: 10, padding: '9px 18px', color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer', fontFamily: 'Outfit,sans-serif' }}>
          <MdArrowBack /> Back
        </button>
      </div>

      <div style={{ maxWidth: 820, margin: '0 auto', background: 'var(--surface-subtle)', border: '1px solid var(--border-color-hover)', borderRadius: 20, padding: '40px 48px', color: 'var(--text-primary)', lineHeight: 1.8 }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: 'linear-gradient(135deg,#4f8ef7,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MdGavel style={{ color: '#fff', fontSize: 28 }} />
          </div>
          <div>
            <h1 style={{ fontSize: 30, fontWeight: 800, margin: 0, background: 'linear-gradient(135deg,var(--text-primary),var(--color-secondary))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Terms & Conditions</h1>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>Last Updated: {LAST_UPDATED}</p>
          </div>
        </div>

        <div style={{ height: 1, background: 'var(--border-color)', margin: '24px 0' }} />

        <p style={{ color: 'var(--text-secondary)', marginBottom: 28 }}>
          Please read these Terms and Conditions carefully before using HCEN. By accessing or using our platform, you agree to be bound by these terms. If you do not agree, please do not use HCEN.
        </p>

        {[
          {
            title: '1. Acceptance of Terms',
            content: [
              'By registering, logging in, or using any HCEN service, you confirm that you are at least 18 years old (or have parental consent).',
              'You agree to these Terms and our Privacy Policy.',
              'HCEN reserves the right to update these Terms at any time with notice.',
            ]
          },
          {
            title: '2. About HCEN',
            content: [
              'HCEN is a healthcare technology platform that connects patients with hospitals for appointment booking, emergency assistance, and health guidance.',
              'HCEN is NOT a medical service provider. We are a technology intermediary between patients and healthcare providers.',
              'All medical advice from our AI features is for informational purposes only and does not replace professional medical consultation.',
            ]
          },
          {
            title: '3. Patient Responsibilities',
            content: [
              'You must provide accurate personal and health information during registration and booking.',
              'You are responsible for attending appointments you have booked.',
              'Cancellation must be done at least 2 hours before the appointment time.',
              'Misuse of emergency features (false alarms) may result in account suspension.',
              'You must not share your OTP or account credentials with anyone.',
            ]
          },
          {
            title: '4. Hospital Services',
            content: [
              'HCEN connects you to registered hospitals. The quality of medical services is the responsibility of the hospital.',
              'Consultation fees are set by individual hospitals. HCEN collects payment on their behalf.',
              'Appointment availability depends on the hospital\'s schedule and doctor availability.',
              'HCEN does not guarantee specific wait times or treatment outcomes.',
            ]
          },
          {
            title: '5. Payments & Refunds',
            content: [
              'All payments are processed securely through Razorpay payment gateway (PCI-DSS Level 1 certified).',
              'Consultation fees are non-refundable once the appointment is confirmed and attended.',
              'If a hospital cancels your appointment, a full refund will be processed within 5-7 business days.',
              'If you cancel at least 2 hours before appointment: full refund.',
              'If you cancel less than 2 hours before appointment: no refund (cancellation fee applies).',
            ]
          },
          {
            title: '6. AI Health Features',
            content: [
              'The AI health chat, symptom checker, and report analyser are informational tools only.',
              'These features do not provide medical diagnosis or replace a qualified doctor.',
              'Always consult a licensed medical professional for any health concerns.',
              'HCEN is not liable for any decisions made based solely on AI-generated health guidance.',
            ]
          },
          {
            title: '7. Emergency Services',
            content: [
              'The Emergency Alert feature connects you to registered hospitals only.',
              'For life-threatening emergencies, always call 108 (Emergency Ambulance) first.',
              'HCEN emergency alerts are not a substitute for government emergency services.',
              'Intentional misuse of emergency alerts is a serious violation and may lead to legal action.',
            ]
          },
          {
            title: '8. Privacy & Data',
            content: [
              'Your data is handled in accordance with our Privacy Policy.',
              'By using HCEN, you consent to collection and processing of personal and health data as described in our Privacy Policy.',
              'You can request deletion of your data at any time by contacting ' + EMAIL,
            ]
          },
          {
            title: '9. Prohibited Activities',
            content: [
              'You must not use HCEN to submit false or misleading health information.',
              'You must not attempt to hack, reverse engineer, or disrupt HCEN\'s systems.',
              'You must not impersonate a healthcare provider or HCEN staff.',
              'You must not use HCEN for any illegal purpose.',
            ]
          },
          {
            title: '10. Limitation of Liability',
            content: [
              'HCEN is not liable for the quality, accuracy, or outcome of medical services provided by hospitals.',
              'HCEN is not liable for technical issues caused by internet connectivity or device problems.',
              'Our maximum liability for any claim shall not exceed the amount you paid for the specific service in question.',
            ]
          },
          {
            title: '11. Governing Law',
            content: [
              'These Terms are governed by the laws of India.',
              'Any disputes will be subject to the exclusive jurisdiction of courts in India.',
            ]
          },
          {
            title: '12. Contact',
            content: [
              `For legal queries: ${EMAIL}`,
              `${COMPANY}`,
            ]
          },
        ].map(section => (
          <div key={section.title} style={{ marginBottom: 28 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#4f8ef7', marginBottom: 10 }}>{section.title}</h2>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {section.content.map((item, i) => (
                <li key={i} style={{ color: 'var(--text-secondary)', fontSize: 14.5, marginBottom: 6 }}>{item}</li>
              ))}
            </ul>
          </div>
        ))}

        <div style={{ height: 1, background: 'var(--border-color)', margin: '28px 0' }} />
        <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center' }}>
          · {new Date().getFullYear()} {COMPANY} · {EMAIL}
        </p>
      </div>
    </div>
  );
}
